/** Authorization code + PKCE against the Cognito hosted UI. No client secret, tokens in
 *  sessionStorage (cleared when the tab closes), ID token is what the API verifies. */

function base64url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomString(length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64url(bytes.buffer).slice(0, length);
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(digest);
}

export interface TokenSet {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  /** Epoch milliseconds. */
  expires_at: number;
}

const KEY = "humindly.tokens";
const PKCE_KEY = "humindly.pkce";

export function loadTokens(): TokenSet | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as TokenSet) : null;
  } catch {
    return null;
  }
}

export function saveTokens(t: TokenSet | null) {
  try {
    if (t) sessionStorage.setItem(KEY, JSON.stringify(t));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable (private mode): the session lives in memory only.
  }
}

export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

export async function beginLogin(domain: string, clientId: string, returnTo: string) {
  const verifier = randomString(64);
  const state = randomString(24);
  const challenge = await challengeFor(verifier);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, returnTo }));
  const u = new URL(`${domain.replace(/\/$/, "")}/oauth2/authorize`);
  u.searchParams.set("client_id", clientId);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("scope", "openid email profile");
  u.searchParams.set("redirect_uri", redirectUri());
  u.searchParams.set("state", state);
  u.searchParams.set("code_challenge", challenge);
  u.searchParams.set("code_challenge_method", "S256");
  window.location.assign(u.toString());
}

export async function completeLogin(domain: string, clientId: string, params: URLSearchParams): Promise<{ tokens: TokenSet; returnTo: string }> {
  const code = params.get("code");
  const state = params.get("state");
  const raw = sessionStorage.getItem(PKCE_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  if (!code || !state || !raw) throw new Error("login did not start here");
  const pkce = JSON.parse(raw) as { verifier: string; state: string; returnTo: string };
  if (pkce.state !== state) throw new Error("login state mismatch");
  const body = new URLSearchParams({ grant_type: "authorization_code", client_id: clientId, code, redirect_uri: redirectUri(), code_verifier: pkce.verifier });
  const res = await fetch(`${domain.replace(/\/$/, "")}/oauth2/token`, { method: "POST", headers: { "content-type": "application/x-www-form-urlencoded" }, body });
  if (!res.ok) throw new Error(`token exchange failed: ${res.status}`);
  const t = (await res.json()) as { id_token: string; access_token: string; refresh_token?: string; expires_in: number };
  const tokens = { id_token: t.id_token, access_token: t.access_token, refresh_token: t.refresh_token, expires_at: Date.now() + t.expires_in * 1000 };
  saveTokens(tokens);
  return { tokens, returnTo: pkce.returnTo || "/" };
}

export async function refresh(domain: string, clientId: string, current: TokenSet): Promise<TokenSet | null> {
  if (!current.refresh_token) return null;
  const body = new URLSearchParams({ grant_type: "refresh_token", client_id: clientId, refresh_token: current.refresh_token });
  const res = await fetch(`${domain.replace(/\/$/, "")}/oauth2/token`, { method: "POST", headers: { "content-type": "application/x-www-form-urlencoded" }, body });
  if (!res.ok) return null;
  const t = (await res.json()) as { id_token: string; access_token: string; expires_in: number };
  const next = { ...current, id_token: t.id_token, access_token: t.access_token, expires_at: Date.now() + t.expires_in * 1000 };
  saveTokens(next);
  return next;
}

export function logoutUrl(domain: string, clientId: string): string {
  const u = new URL(`${domain.replace(/\/$/, "")}/logout`);
  u.searchParams.set("client_id", clientId);
  u.searchParams.set("logout_uri", `${window.location.origin}/`);
  return u.toString();
}
